import { ErrorHandler, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { EmptyComponent } from './components/empty.component';
import { NotfoundComponent } from './components/notfound/notfound.component';
import { UserProfileComponent } from './components/profile/profile.component';
import { BypassSanitizeHtml, BypassSanitizeStyle, BypassSanitizeUrl } from './pipes/sanitize.pipe';
import { JoinPipe } from './pipes/join.pipe';
import { CopyDirective } from './directives/copy.directive';
import { GoogleAnalyticsEventsDirective } from './directives/google-analytics-events.directive';
import { LocationComponent } from './components/location/location.component';
import { ValidDatePipe } from './pipes/valid-date.pipe';
import { LoginComponent } from './components/login/login.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { FormHintComponent } from './components/form-hint/form-hint.component';
import { ConfirmDirective } from './directives/confirm.directive';
import { ScrollToDirective } from './directives/scroll-to.directive';
import { NumericDirective } from './directives/numeric.directive';
import { ResponsiveTabComponent } from './components/responsive-tab/responsive-tab.component';
import { FilterPipe } from './pipes/filter.pipe';
import { GlobalBannerComponent } from './components/global-banner/global-banner.component';
import { DebounceDirective } from './directives/debounce.directive';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { CompletenessRingComponent } from './components/completeness-ring/completeness-ring.component';
import { WhatsappMessageComponent } from './components/whatsapp-message/whatsapp-message.component';
import { FlexLayoutComponent } from './components/flex-layout/flex-layout.component';
import { FooterComponent } from './components/footer/footer.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { FavoriteListComponent } from './components/favorite-list/favorite-list.component';
import { ResumeComponent } from './components/resume/resume.component';
import { JobDetailComponent } from './components/job-detail/job-detail.component';
import { JobSummaryComponent } from './components/job-summary/job-summary.component';
import { JobListNewComponent } from './components/job-list-new/job-list-new.component';
import { EmbedIgComponent } from './components/embed-ig/embed-ig.component';
import { SubscribeSearchComponent } from './components/subscribe-search/subscribe-search.component';
import { FocusLayoutComponent } from './components/focus-layout/focus-layout.component';
import { CompleteResumePropmptComponent } from './components/complete-resume-propmpt/complete-resume-propmpt.component';
import { ClickStopPropagation } from './directives/click-stop-propagation.directive';
import { SelectorComponent } from './components/selector/selector.component';
import { NumberRangeComponent } from './components/number-range/number-range.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { SearchBoxComponent } from './components/search-box/search-box.component';
import { StickyBarDirective } from './directives/sticky-bar.directive';
import { SplitPipe } from './pipes/split.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { EnamadComponent } from './components/enamad/enamad.component';
import { AvatarPipe } from './pipes/avatar.pipe';
import { QuestionerComponent } from './components/questioner/questioner.component';
import { PaymentCheckComponent } from '../shared-panel/components/payment-check/payment-check.component';
import { ManualPaymentModalComponent } from '../shared-panel/components/manual-payment-modal/manual-payment-modal.component';
import { ShareDirective } from './directives/share.directive';
import { PaymentDetailComponent } from './components/payment-detail/payment-detail.component';
import { PushActivatorComponent } from './components/push-activator/push-activator.component';
import { NpsComponent } from './components/nps/nps.component';
import { MapPipe } from './pipes/map.pipe';
import { GeneralPollWizardComponent } from './components/general-poll-wizard/general-poll-wizard.component';
import { OptionSelectorComponent } from './components/option-selector/option-selector.component';
import { ReportJobComponent } from './components/report-job/report-job.component';
import { VanguardPackagesComponent } from './components/vanguard-packages/vanguard-packages.component';
import { JsonldComponent } from './components/jsonld/jsonld.component';
import { SortPipe } from './pipes/sort.pipe';
import { AgreementViewComponent } from './components/agreement-view/agreement-view.component';
import { SeekerJobPaymentComponent } from './components/seeker-job-payment/seeker-job-payment.component';
import { PaymentComponent } from './components/karpasha-payment/karpasha-payment.component';
import { UrlBlobPipe } from './pipes/url-blob.pipe';
import { ObjectRendererComponent } from './components/object-renderer/object-renderer.component';
import { DueStatusPipe } from './pipes/due-status.pipe';
import { CleanPasteDirective } from './directives/clean-paste.directive';
import { ContextMenuComponent } from './components/context-menu/context-menu.component';
import { DynamicLoaderComponent } from './components/dynamic-loader/dynamic-loader.component';
import { ValuePipe } from './pipes/value.pipe';
import { HighlightDirective } from './directives/highlight.directive';
import { CalendarComponent } from '../shared-panel/components/calendar/calendar.component';
import { ReadablePipe } from './pipes/readable.pipe';
import { SmsDirective } from './directives/sms.directive';
import { FileSizePipe } from './pipes/file-size.pipe';
import { FavIconPipe } from './pipes/fav-icon.pipe';
import { SuggesterComponent } from './components/suggester/suggester.component';
import { CoreModule } from 'src/app/core/core.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ManageHttpInterceptor } from './interceptors/http.interceptor';
import { EncodeParamsInterceptor } from './interceptors/encode-params.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { CustomRouteReuseStrategy } from './overrides/route-reuse-strategy';
import { ResumeOnboardingModalComponent } from '../seeker/components/resume-onboarding-modal/resume-onboarding-modal.component';

const shared = [
  EmptyComponent, NotfoundComponent, UserProfileComponent, BypassSanitizeHtml, SmsDirective,
  BypassSanitizeStyle, BypassSanitizeUrl, JoinPipe, SplitPipe, SortPipe, CopyDirective, CleanPasteDirective, UrlBlobPipe,
  GoogleAnalyticsEventsDirective, LocationComponent, EmbedIgComponent, SubscribeSearchComponent, ShareDirective,
  ValidDatePipe, LoginComponent, AutoFocusDirective, FavoriteListComponent, ClickStopPropagation, ValuePipe, ReadablePipe,
  FormHintComponent, FilterPipe, MapPipe, ResumeComponent, JobListNewComponent, BreadcrumbComponent,
  GlobalBannerComponent, DebounceDirective, JobDetailComponent, JobSummaryComponent, HighlightPipe, AvatarPipe,
  SpinnerComponent, ConfirmDirective, NumericDirective, ResponsiveTabComponent, StickyBarDirective, ObjectRendererComponent,
  FormHintComponent, GlobalBannerComponent, WhatsappMessageComponent, AutocompleteComponent, NpsComponent,
  CompletenessRingComponent, FlexLayoutComponent, FocusLayoutComponent, ScrollToDirective, FooterComponent, PaginationComponent,
  CompleteResumePropmptComponent, SelectorComponent, NumberRangeComponent, SearchBoxComponent, QuestionerComponent,
  VanguardPackagesComponent, PaymentCheckComponent, ManualPaymentModalComponent, EnamadComponent, PaymentDetailComponent,
  PushActivatorComponent, GeneralPollWizardComponent, OptionSelectorComponent, ReportJobComponent, JsonldComponent,
  AgreementViewComponent, SeekerJobPaymentComponent, PaymentComponent, DueStatusPipe, ContextMenuComponent, DynamicLoaderComponent,
  HighlightDirective, CalendarComponent, FileSizePipe, FavIconPipe, SuggesterComponent, ResumeOnboardingModalComponent
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgSelectModule,
    TooltipModule.forRoot(),
    NgxSmartModalModule.forRoot(),
    InfiniteScrollModule,
    CoreModule
  ],
  declarations: [shared],
  exports: [shared],
  providers: [
    // HttpCancelService,
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: ManageHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EncodeParamsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorInterceptor }
  ],
})
export class SharedModule { }
